import React, { useCallback, useEffect, useState } from "react";
import { History, RotateCcw, Loader2, GitCompare } from "lucide-react";
import api from "../../../lib/api";
import { fmtDate } from "../ui";
import { Modal, Label, errText, notifyCmsChanged } from "./cmsShared";
import { schemaFor } from "./schemas";
import VersionDiff from "./VersionDiff";

function CompareModal({ item, rev, live, isReviewer, onClose, onRestored, flash }) {
  const [busy, setBusy] = useState(false);
  const restore = async () => {
    const overwrite = !isReviewer && item.draft ? "\n\nThis will replace the draft currently in progress." : "";
    const msg = isReviewer
      ? `Publish version ${rev.version} to the live site now?`
      : `Restore version ${rev.version} as a draft and send it for review?${overwrite}`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/cms/content/${item.id}/revisions/${rev.id}/restore`);
      notifyCmsChanged();
      onRestored(data);
      flash(isReviewer ? `Version ${rev.version} is live again.` : `Version ${rev.version} submitted for review.`);
      onClose();
    } catch (e) { flash(errText(e), "error"); setBusy(false); }
  };
  const dataL = rev.data || {};
  const dataR = live?.data || {};
  return (
    <Modal title={`Version ${rev.version} vs live`} onClose={onClose} wide testid="compare-modal">
      <div className="mb-3 grid grid-cols-2 gap-4 px-3">
        <div data-testid="compare-left-head">
          <p className="font-display text-lg font-semibold text-sapphire-deep">Version {rev.version}</p>
          <p className="font-sans text-xs text-stonegrey">{fmtDate(rev.published_at, true)} · {rev.published_by_name}</p>
        </div>
        <div data-testid="compare-right-head">
          <p className="font-display text-lg font-semibold text-emerald">{live ? `Current live · v${live.version}` : "Not currently published"}</p>
          {live && <p className="font-sans text-xs text-stonegrey">{fmtDate(live.published_at, true)} · {live.published_by_name}</p>}
        </div>
      </div>
      <VersionDiff schema={schemaFor(item)} left={dataL} right={dataR} />
      <div className="sticky bottom-0 mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-sapphire-deep/10 bg-white pt-4">
        {!isReviewer && <p className="mr-auto font-sans text-xs text-stonegrey">As an Author, your restore goes to a Reviewer before it is published.</p>}
        <button onClick={onClose} className="btn-dark">Close</button>
        <button onClick={restore} disabled={busy || (live && rev.version === live.version)} className="btn-sapphire" data-testid="compare-restore-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />}
          {isReviewer ? "Restore to live" : "Restore as draft for review"}
        </button>
      </div>
    </Modal>
  );
}

export default function VersionHistory({ item, caps, onRestored, flash }) {
  const [revs, setRevs] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get(`/cms/content/${item.id}/revisions`).then(({ data }) => setRevs(data)), [item.id]);
  useEffect(() => { load(); }, [load, item.published?.version]);

  if (!revs || !revs.length) return null;
  const liveVersion = item.published?.version;
  return (
    <div data-testid="version-history">
      <Label><span className="inline-flex items-center gap-1.5"><History size={12} /> Version history</span></Label>
      <ul className="max-h-72 divide-y divide-sapphire-deep/5 overflow-y-auto">
        {revs.map((r) => {
          const isLive = item.published && r.version === liveVersion;
          return (
            <li key={r.id} className="flex items-center gap-3 py-2.5" data-testid={`version-row-${r.version}`}>
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-sans text-xs font-bold ${isLive ? "bg-emerald text-parchment" : "bg-sapphire-deep/8 text-sapphire-deep"}`}>v{r.version}</span>
              <div className="min-w-0 flex-1 font-sans text-xs">
                <p className="truncate text-sapphire-deep">{r.published_by_name}{isLive && <span className="ml-1.5 font-semibold text-emerald">· Live</span>}</p>
                <p className="text-stonegrey">{fmtDate(r.published_at, true)}{r.restored_from ? ` · restored v${r.restored_from}` : ""}</p>
              </div>
              {!isLive && (
                <button onClick={() => setOpen(r)} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-sans text-[11px] font-semibold uppercase tracking-wide text-sapphire-deep ring-1 ring-sapphire-deep/15 transition-colors hover:bg-gold/20" data-testid={`version-compare-${r.version}`}>
                  <GitCompare size={12} /> Compare
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {open && (
        <CompareModal item={item} rev={open} live={item.published} isReviewer={!!caps.reviewer}
          onClose={() => setOpen(null)} onRestored={(d) => { onRestored(d); load(); }} flash={flash} />
      )}
    </div>
  );
}
